import axios from 'axios';
import { API_URL } from './constants';

// Default axios instance for public APIs (no authentication required)
// For public content viewing, contact forms, etc.
const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    withCredentials: false, // Public APIs don't need credentials
    timeout: 30000, // 30 seconds timeout
});

// Authenticated axios instance for APIs that require authentication
// Use this for protected endpoints that need cookies/tokens
export const axiosAuth = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    withCredentials: true, // Required for authenticated endpoints
    timeout: 30000,
});

// Request interceptors for both instances
const requestInterceptor = (config) => config;
const requestErrorInterceptor = (error) => Promise.reject(error);

axiosInstance.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
axiosAuth.interceptors.request.use(requestInterceptor, requestErrorInterceptor);

// Shared error handler function
const handleError = (error) => {
    // Handle CORS errors specifically
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        // This often indicates a CORS issue
        error.isCorsError = true;
        error.userMessage = 'Unable to connect to the server. Please check your connection or contact support if the problem persists.';
    } else if (error.response) {
        // Server responded with error status
        error.userMessage = error.response.data?.message || 'An error occurred. Please try again.';
    } else if (error.request) {
        // Request was made but no response received
        error.isCorsError = true;
        error.userMessage = 'No response from server. This may be a CORS configuration issue.';
    } else {
        // Something else happened
        error.userMessage = 'An unexpected error occurred. Please try again.';
    }
    return Promise.reject(error);
};

// Response interceptor for public API instance
axiosInstance.interceptors.response.use(
    (response) => response,
    handleError
);

// Response interceptor for authenticated API instance
axiosAuth.interceptors.response.use(
    (response) => response,
    handleError
);

export default axiosInstance;

